/**
 * Taken from https://github.com/gdenisov/cardinal-spline-js
 * and modified to fit our purpose (and lint settings).
 */

const _ = require("lodash");

/**
 * Draws a cardinal spline through given point array. Points must be arranged
 * as: [x1, y1, x2, y2, ..., xn, yn]. It adds the points to the current path.
 *
 * The method continues previous path of the context. If you don't want that
 * then you need to use moveTo() with the first point from the input array.
 *
 * The points for the cardinal spline are returned as a new array.
 *
 * @param {CanvasRenderingContext2D} ctx - context to use
 * @param {Array} points - point array
 * @param {Object} [options={}] - options for [tension=0.5] and [numOfSeg=20]
 * @returns {Float32Array} New array with the calculated points that was added to the path
 */
const curve = (ctx, points, options = {}) => {
	const {
		tension = 0.5, // typically between [0.0, 1.0] but can be exceeded
		numOfSeg = 20, // number of segments between two points (line resolution)
	} = options;

	if (!_.isNumber(tension)) throw new Error("Tension must be a number");
	if (!_.isInteger(numOfSeg) || numOfSeg <= 0)
		throw new Error("Number of segments must be a positive integer");

	ctx.save();
	ctx.moveTo(points[0], points[1]);

	let pts = _.clone(points);
	let l = points.length;
	let rPos = 0;
	const rLen = (l - 2) * numOfSeg + 2 + 2 * numOfSeg;
	const res = new Float32Array(rLen);
	const cache = new Float32Array((numOfSeg + 2) * 4);
	let cachePtr = 4;

	// insert end point as first point
	pts.unshift(points[l - 2], points[l - 1]);
	// first point as last point
	pts.push(points[0], points[1]);

	// cache inner-loop calculations as they are based on t alone
	// 1,0,0,0...
	cache[0] = 1;

	for (let i = 1; i < numOfSeg; ++i) {
		const st = i / numOfSeg,
			st2 = st * st,
			st3 = st2 * st,
			st23 = st3 * 2,
			st32 = st2 * 3;

		cache[cachePtr++] = st23 - st32 + 1; // c1
		cache[cachePtr++] = st32 - st23; // c2
		cache[cachePtr++] = st3 - 2 * st2 + st; // c3
		cache[cachePtr++] = st3 - st2; // c4
	}

	// 0,1,0,0...
	cache[++cachePtr] = 1;

	// calc. points
	parse();

	pts = [];
	pts.push(points[l - 4], points[l - 3], points[l - 2], points[l - 1]); // second last and last
	pts.push(points[0], points[1], points[2], points[3]); // first and second
	parse(4);

	function parse(length = l) {
		for (let i = 2, t; i < length; i += 2) {
			const pt1 = pts[i],
				pt2 = pts[i + 1],
				pt3 = pts[i + 2],
				pt4 = pts[i + 3],
				t1x = (pt3 - pts[i - 2]) * tension,
				t1y = (pt4 - pts[i - 1]) * tension,
				t2x = (pts[i + 4] - pt1) * tension,
				t2y = (pts[i + 5] - pt2) * tension;

			for (t = 0; t < numOfSeg; t++) {
				const c = t << 2, //t * 4;
					c1 = cache[c],
					c2 = cache[c + 1],
					c3 = cache[c + 2],
					c4 = cache[c + 3];

				res[rPos++] = c1 * pt1 + c2 * pt3 + c3 * t1x + c4 * t2x;
				res[rPos++] = c1 * pt2 + c2 * pt4 + c3 * t1y + c4 * t2y;
			}
		}
	}

	// add last point
	res[rPos++] = points[0];
	res[rPos] = points[1];

	// add lines to path
	l = res.length;
	ctx.beginPath();
	for (let i = 0; i < l; i += 2) ctx.lineTo(res[i], res[i + 1]);
	ctx.closePath();
	ctx.restore();

	return res;
};

module.exports = {
	curve,
};
